/* WelcomeUpsellPage — post-checkout welcome funnel (LIGHT theme).
 *
 * Shown when Stripe redirects back to /welcome?session_id=... after the
 * initial plan subscription succeeds. Steps:
 *
 *   1. Hero ("You're in")
 *   2. Done-For-You Bookkeeping upgrade ($299/mo) — skippable
 *   3. Storage tier — skippable
 *   4. Platinum CFO upgrade (20% off first 3 months) — skippable
 *
 * Every step has a big, obvious "No thanks, continue" button.
 *
 * Guard: if the visitor has NOT completed plan checkout yet (no active
 * subscription), we show the add-card retry screen instead of upsells —
 * fixes the bug where users were dumped into the funnel without ever
 * seeing Stripe Checkout.
 */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { apiFetch } from '../fetchInterceptor';
import { fireConversion } from '../lib/gtagConversions';
import { metaTrack } from '../lib/metaPixel';
import {
  CheckCircle2, HardDrive, Sparkles, ArrowRight, Loader2,
  PartyPopper, X, Crown, BookOpenCheck,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STORAGE_TIERS = [
  { id: 'storage_50gb', label: '+50 GB', price: 4, blurb: 'For solo brands' },
  { id: 'storage_250gb', label: '+250 GB', price: 10, blurb: 'For growing businesses' },
  { id: 'storage_1tb', label: '+1 TB', price: 24, blurb: 'For agencies & firms' },
  { id: 'storage_5tb', label: '+5 TB', price: 99, blurb: 'For high-volume teams' },
];

const STEPS = ['hero', 'dfy_bookkeeping', 'storage', 'platinum_upgrade', 'done'];

// Map a Stripe upsell offer id back to its funnel step.
function offerToStep(offer) {
  if (!offer) return null;
  if (offer.startsWith('storage')) return 'storage';
  if (offer === 'done_for_you_upgrade') return 'dfy_bookkeeping';
  if (offer === 'platinum_upgrade') return 'platinum_upgrade';
  if (offer.startsWith('tax_return')) return 'dfy_bookkeeping'; // legacy
  return STEPS.includes(offer) ? offer : null;
}

export default function WelcomeUpsellPage() {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [planId, setPlanId] = useState(null);
  // null = still checking, true = must finish checkout, false = subscribed
  const [needsCheckout, setNeedsCheckout] = useState(null);

  const params = useMemo(
    () => (typeof window === 'undefined' ? new URLSearchParams() : new URLSearchParams(window.location.search)),
    [],
  );

  const cancelled = params.get('cancelled') === 'true';
  const cancelledPlan = params.get('plan') || 'starter';
  const cancelledCadence = params.get('cadence') === 'annual' ? 'annual' : 'monthly';
  // A session_id means the user just came back from a SUCCESSFUL checkout.
  const hasSession = Boolean(params.get('session_id'));

  // Stamp a session flag when we render the retry screen so TrialGateBanner
  // suppresses its auto-redirect for 15 min (breaks redirect loops).
  useEffect(() => {
    if (!cancelled) return;
    try {
      window.sessionStorage.setItem('trial_gate_recently_cancelled', String(Date.now()));
    } catch { /* ignore */ }
  }, [cancelled]);

  const retryCheckout = useCallback(async () => {
    setBusy(true);
    try {
      const res = await apiFetch(`${API_URL}/api/billing/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          item_type: 'plan',
          item_id: cancelledPlan,
          origin_url: window.location.origin,
          cadence: cancelledCadence,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.url) {
        window.location.href = d.url;
        return;
      }
      toast.error(d.detail || 'Could not restart checkout.');
    } catch (e) {
      toast.error(e.message || 'Network error');
    }
    setBusy(false);
  }, [cancelledPlan, cancelledCadence]);

  // Advance one step when returning from an upsell checkout.
  useEffect(() => {
    const stepDone = params.get('step_done');
    const stepSkipped = params.get('step_skipped');
    const last = stepDone || stepSkipped;
    if (last) {
      const step = offerToStep(last);
      const idx = step ? STEPS.indexOf(step) : -1;
      if (idx >= 0) {
        setStepIdx(Math.min(idx + 1, STEPS.length - 1));
        if (stepDone) toast.success('Added — thanks for the upgrade!');
      }
      window.history.replaceState({}, '', '/welcome');
    }
  }, [params]);

  // Pull plan + subscription state. If the user has no subscription and
  // didn't just complete checkout, force the add-card retry screen.
  // A session_id in the URL is only trusted after SERVER-SIDE verification
  // (ownership + completed status) — a forged or abandoned session keeps
  // the retry screen up.
  useEffect(() => {
    (async () => {
      try {
        if (hasSession) {
          const sid = params.get('session_id');
          try {
            const vr = await apiFetch(`${API_URL}/api/billing/checkout/status/${sid}`);
            if (vr.ok) {
              const v = await vr.json();
              if (v.status === 'complete') {
                setNeedsCheckout(false);
                // Google Ads checkout conversion — no-op until the label
                // is set in lib/gtagConversions.js. Uses the Stripe
                // session_id as the dedupe transaction_id.
                fireConversion('checkout', {
                  transactionId: sid,
                  value: (typeof v.amount_total === 'number' ? v.amount_total / 100 : undefined),
                });
                const usageRes = await apiFetch(`${API_URL}/api/billing/ai-usage`);
                if (usageRes.ok) {
                  const u = await usageRes.json();
                  setPlanId(u.plan_id);
                  // Meta StartTrial — the 7-day trial just started. Plan/price
                  // (value, predicted_ltv, content_*) resolved server-side.
                  metaTrack('StartTrial', { planId: u.plan_id, cadence: v.cadence || 'monthly' });
                }
                return;
              }
            }
          } catch { /* fall through to trial-status */ }
        }
        const [usageRes, trialRes] = await Promise.all([
          apiFetch(`${API_URL}/api/billing/ai-usage`),
          apiFetch(`${API_URL}/api/billing/trial-status`),
        ]);
        if (usageRes.ok) {
          const d = await usageRes.json();
          setPlanId(d.plan_id);
        }
        if (trialRes.ok) {
          const t = await trialRes.json();
          setNeedsCheckout(Boolean(t.must_start_trial) && !cancelled);
        } else {
          setNeedsCheckout(false);
        }
      } catch {
        setNeedsCheckout(false);
      }
    })();
  }, [hasSession, cancelled, params]);

  // Auto-create the workspace from the pre-paywall stash. New signups type
  // their business name BEFORE checkout, but the backend paywall (402
  // PLAN_REQUIRED) blocks creation until they've paid — the name is stashed
  // in localStorage. Once checkout is confirmed, silently create the
  // workspace here so the user is never asked to name their business twice.
  const wsAutoCreateRef = useRef(false);
  useEffect(() => {
    if (needsCheckout !== false) return;
    if (wsAutoCreateRef.current) return;
    wsAutoCreateRef.current = true;
    (async () => {
      let pendingName = null;
      try { pendingName = window.localStorage.getItem('pending_business_name'); } catch { /* ignore */ }
      if (!pendingName || !pendingName.trim()) return;
      try {
        const listRes = await apiFetch(`${API_URL}/api/workspaces`);
        if (!listRes.ok) return; // not authenticated — AppLayout prefill covers it
        const existing = await listRes.json();
        if (existing.length > 0) {
          try { window.localStorage.removeItem('pending_business_name'); } catch { /* ignore */ }
          return;
        }
        const createRes = await apiFetch(`${API_URL}/api/workspaces`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: pendingName.trim(),
            currency: 'USD',
            workspace_type: 'business',
            industry_template: 'general',
            template_key: 'small_business',
          }),
        });
        if (createRes.ok) {
          const nw = await createRes.json();
          try {
            window.localStorage.removeItem('pending_business_name');
            window.localStorage.setItem('currentWorkspaceId', nw.workspace_id);
          } catch { /* ignore */ }
        }
      } catch { /* AppLayout prefill fallback */ }
    })();
  }, [needsCheckout]);

  const next = useCallback(() => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1)), []);
  const skip = useCallback(() => next(), [next]);

  const startUpsell = useCallback(async (offer) => {
    setBusy(true);
    try {
      const res = await apiFetch(`${API_URL}/api/billing/upsell`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ offer, origin_url: window.location.origin }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.url) {
        window.location.href = d.url;
        return;
      }
      toast.error(d.detail || 'Could not start upsell checkout.');
    } catch (e) {
      toast.error(e.message || 'Network error');
    }
    setBusy(false);
  }, []);

  const current = STEPS[stepIdx];
  const onHigherPlan = planId === 'done_for_you' || planId === 'platinum';

  // Skip steps that don't apply to the current plan.
  useEffect(() => {
    if (current === 'dfy_bookkeeping' && onHigherPlan) next();
    if (current === 'platinum_upgrade' && planId === 'platinum') next();
  }, [current, planId, onHigherPlan, next]);

  const showRetry = cancelled || needsCheckout === true;
  // Wait for the subscription check before flashing upsell content.
  const checking = !cancelled && needsCheckout === null;

  return (
    <div className="min-h-screen bg-[#F5F7F6] text-[#18202D] flex items-center justify-center px-6 py-16 relative overflow-hidden" data-testid="welcome-page">
      {/* Soft background wash */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-emerald-200/40 blur-[120px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[700px] h-[700px] rounded-full bg-emerald-100/50 blur-[140px]" />
      </div>

      <div className="relative z-10 w-full max-w-2xl">
        {checking && (
          <div className="flex justify-center py-24" data-testid="welcome-loading">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        )}

        {/* Add-card retry screen — checkout not completed yet. */}
        {!checking && showRetry && (
          <UpsellCard
            data-testid="welcome-cancelled-retry"
            hideSkipLink
            icon={X}
            overline="ONE LAST STEP"
            title={<>Almost there — <span className="text-emerald-600">add your card.</span></>}
            body={
              <>
                <p>Your payment details haven&apos;t been added yet, so your trial hasn&apos;t started. CountBucks requires a card on file — your trial is <span className="text-[#18202D] font-semibold">free for 7 days</span>, and you can cancel anytime before day 8 to avoid being charged.</p>
                <p className="mt-3 text-[#596273] text-sm">No card = no access to your books, banking, or AI tools.</p>
              </>
            }
            primary={{
              label: busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add card & start free trial',
              onClick: retryCheckout,
              disabled: busy,
              testId: 'welcome-retry-checkout-btn',
            }}
            secondary={{
              label: 'Sign out',
              onClick: async () => {
                try {
                  await apiFetch(`${API_URL}/api/auth/logout`, { method: 'POST' });
                } catch { /* ignore */ }
                window.location.href = '/';
              },
              testId: 'welcome-retry-signout-btn',
            }}
          />
        )}

        {/* Progress dots */}
        {!checking && !showRetry && current !== 'done' && (
          <div className="flex justify-center gap-2 mb-10">
            {STEPS.slice(0, -1).map((s, i) => (
              <span
                key={s}
                className={`h-1.5 rounded-full transition-all ${i === stepIdx ? 'w-8 bg-emerald-500' : i < stepIdx ? 'w-2 bg-emerald-500/60' : 'w-2 bg-[#D6DDD9]'}`}
              />
            ))}
          </div>
        )}

        {!checking && !showRetry && current === 'hero' && (
          <UpsellCard
            data-testid="welcome-hero"
            icon={PartyPopper}
            overline="WELCOME TO COUNTBUCKS"
            title={<>You&apos;re in. <span className="text-emerald-600">Your workspace is live.</span></>}
            body="Before you dive in — here are a couple of optional extras our most successful customers add. Everything can be skipped, and nothing is charged unless you choose it."
            primary={{ label: 'See optional extras', onClick: next, testId: 'welcome-hero-continue' }}
            secondary={{ label: 'Skip to my dashboard', onClick: () => navigate('/dashboard'), testId: 'welcome-hero-skip' }}
          />
        )}

        {!checking && !showRetry && current === 'dfy_bookkeeping' && !onHigherPlan && (
          <UpsellCard
            data-testid="welcome-dfy"
            icon={BookOpenCheck}
            overline="STEP 1 OF 3 · OPTIONAL"
            title={<>Want us to do the books <span className="text-emerald-600">for you?</span></>}
            body={
              <>
                <p>Upgrade to <strong className="text-[#18202D]">Done-For-You Bookkeeping</strong> and a dedicated CPA or Enrolled Agent handles your monthly bookkeeping — you never categorize a transaction again.</p>
                <ul className="mt-5 space-y-2">
                  <Bullet>Dedicated CPA / EA bookkeeper does all monthly categorization</Bullet>
                  <Bullet>Month-end close delivered in 5 business days</Bullet>
                  <Bullet>White-glove migration from QuickBooks included</Bullet>
                  <Bullet>15 bank accounts · 5,000 AI credits · 150 GB storage</Bullet>
                </ul>
                <div className="mt-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 flex items-baseline justify-between">
                  <span className="text-2xl font-bold text-emerald-700">$299<span className="text-sm font-normal text-emerald-600">/mo</span></span>
                  <span className="text-xs text-[#596273]">Cancel anytime · replaces your current plan</span>
                </div>
              </>
            }
            primary={{
              label: busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Yes, do my books for me',
              onClick: () => startUpsell('done_for_you_upgrade'),
              disabled: busy,
              testId: 'welcome-dfy-yes',
            }}
            secondary={{ label: 'No thanks, continue', onClick: skip, testId: 'welcome-dfy-no' }}
          />
        )}

        {!checking && !showRetry && current === 'storage' && (
          <UpsellCard
            data-testid="welcome-storage"
            icon={HardDrive}
            overline="STEP 2 OF 3 · OPTIONAL"
            title={<>Need extra <span className="text-emerald-600">document storage?</span></>}
            body="Your plan already includes storage. If you scan a lot of receipts and contracts, you can add more — totally optional."
          >
            <div className="mt-5 grid grid-cols-2 gap-3">
              {STORAGE_TIERS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => startUpsell(t.id)}
                  disabled={busy}
                  className="text-left p-4 rounded-xl border border-[#E4E8E6] bg-white hover:bg-emerald-50/60 hover:border-emerald-400 transition-colors disabled:opacity-50"
                  data-testid={`welcome-storage-${t.id}`}
                >
                  <div className="flex items-baseline justify-between">
                    <span className="text-[#18202D] font-semibold text-lg">{t.label}</span>
                    <span className="text-emerald-600 font-mono">${t.price}/mo</span>
                  </div>
                  <p className="mt-1 text-xs text-[#596273]">{t.blurb}</p>
                </button>
              ))}
            </div>
            <div className="mt-6">
              <Button
                size="lg"
                variant="outline"
                onClick={skip}
                disabled={busy}
                data-testid="welcome-storage-skip"
                className="w-full border-[#D6DDD9] text-[#374151] hover:bg-[#F0F3F1] h-12"
              >
                No thanks, continue
              </Button>
            </div>
          </UpsellCard>
        )}

        {!checking && !showRetry && current === 'platinum_upgrade' && planId !== 'platinum' && (
          <UpsellCard
            data-testid="welcome-platinum"
            icon={Crown}
            overline="STEP 3 OF 3 · OPTIONAL · ONE-TIME OFFER"
            title={<>Upgrade to <span className="text-emerald-600">Platinum</span> for 20% off.</>}
            body={
              <>
                <p>Get a <strong className="text-[#18202D]">15-minute monthly strategy call with a business advisor</strong>, unlimited users, 4,000 AI credits, 100 GB storage, 10 bank connections, and advanced reports & exports.</p>
                <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 rounded-lg bg-[#F0F3F1] border border-[#E4E8E6]">
                    <p className="text-[10px] font-mono text-[#7A8494] uppercase tracking-wider">Standard price</p>
                    <p className="text-lg font-bold text-[#374151] line-through opacity-70">$99.99/mo</p>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-300">
                    <p className="text-[10px] font-mono text-emerald-700 uppercase tracking-wider">Your price · first 3 months</p>
                    <p className="text-lg font-bold text-emerald-700">$79.99/mo <span className="text-xs font-normal text-emerald-600">−20%</span></p>
                  </div>
                </div>
                <p className="mt-3 text-xs text-[#7A8494]">Reverts to standard $99.99/mo on month 4. Cancel anytime — no commitment.</p>
              </>
            }
            primary={{
              label: busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Yes, upgrade to Platinum',
              onClick: () => startUpsell('platinum_upgrade'),
              disabled: busy,
              testId: 'welcome-platinum-yes',
            }}
            secondary={{ label: 'No thanks, my plan is fine', onClick: skip, testId: 'welcome-platinum-no' }}
          />
        )}

        {!checking && !showRetry && current === 'done' && (
          <UpsellCard
            data-testid="welcome-done"
            icon={CheckCircle2}
            overline="ALL SET"
            title={<>You&apos;re ready. <span className="text-emerald-600">Let&apos;s get to work.</span></>}
            body="Your CountBucks workspace is live. Connect a bank account, upload your first receipt, or ask the AI Studio anything to get started."
            primary={{
              label: 'Go to my dashboard',
              onClick: () => navigate('/dashboard'),
              testId: 'welcome-done-cta',
            }}
          />
        )}
      </div>
    </div>
  );
}

// ─── Reusable upsell card layout (light theme) ─────────────────────────
function UpsellCard({ icon: Icon, overline, title, body, primary, secondary, children, hideSkipLink, ...rest }) {
  return (
    <div
      className="relative rounded-3xl border border-[#E4E8E6] bg-white p-8 sm:p-10 shadow-xl shadow-emerald-900/5"
      {...rest}
    >
      {secondary && (
        <button
          type="button"
          aria-label="Skip"
          onClick={secondary.onClick}
          data-testid={`${secondary.testId || 'upsell-skip-x'}-x`}
          className="absolute top-4 right-4 text-[#9AA2AE] hover:text-[#18202D] transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      )}
      {Icon && (
        <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 mb-6">
          <Icon className="w-7 h-7" />
        </div>
      )}
      {overline && (
        <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#7A8494]">{overline}</p>
      )}
      <h1 className="mt-3 text-3xl sm:text-4xl font-bold text-[#18202D] tracking-tight leading-[1.1]">{title}</h1>
      {body && <div className="mt-5 text-[#4A5568] leading-relaxed">{body}</div>}
      {children}
      {(primary || secondary) && (
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          {primary && (
            <Button
              size="lg"
              onClick={primary.onClick}
              disabled={primary.disabled}
              data-testid={primary.testId}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold h-12 gap-2"
            >
              {primary.label}
              {!primary.disabled && <ArrowRight className="w-4 h-4" />}
            </Button>
          )}
          {secondary && (
            <Button
              size="lg"
              variant="outline"
              onClick={secondary.onClick}
              disabled={primary?.disabled}
              data-testid={secondary.testId}
              className={`border-[#D6DDD9] text-[#374151] hover:bg-[#F0F3F1] h-12 ${primary ? '' : 'flex-1'}`}
            >
              {secondary.label}
            </Button>
          )}
        </div>
      )}
      {!hideSkipLink && (
        <p className="mt-5 text-[11px] font-mono text-[#9AA2AE] uppercase tracking-wider text-center">
          <button
            type="button"
            onClick={() => { window.location.href = '/dashboard'; }}
            data-testid="welcome-skip-everything"
            className="hover:text-[#18202D] transition-colors uppercase tracking-wider"
          >
            Skip everything · go to dashboard →
          </button>
        </p>
      )}
      {hideSkipLink && (
        <p className="mt-5 text-[11px] font-mono text-[#C4CBC7] uppercase tracking-wider text-center">Card required to continue</p>
      )}
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex gap-3 text-sm text-[#4A5568]">
      <Sparkles className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}
